import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Formik, FormikValues } from "formik";
import { Modal, Select, Switch, Input, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import Button from "../../commons/Button/Button";
import { FormField } from "../../commons/FormField/FormField";
import "../../css/ScrollbarStyles.css";
import useUser from "../../state/hooks/users/useUser";
import TextInput from "../../commons/TextInput/TextInput";
import Typography from "../../commons/Typography/Typography";
import useMe from "../../state/hooks/auth/useMe";
import useUpdateUser from "../../state/hooks/users/useUpdateUser";
import { InputErrorMessage } from "../../commons/error";
import generateRandomPassword from "../../utils/helpers/generatePassword";
import mainUserSchema from "../../models/schemas/main-user.schema";
import useDeleteMainUser from "../../state/hooks/users/useDeleteMainUser";
import getChangedValues from "../../utils/helpers/getChangedValues";
import formatDate from "../../utils/helpers/formatDate";
import { User, ConsentOptOutType, IConsent, IConsentAuditLog } from "../../models/Interface/User.interface";
import useUserExportDownload from "../../state/hooks/users/useUserExportDownload";
import useUserConsents from "../../state/hooks/users/useUserConsents";
import useUpsertConsent from "../../state/hooks/users/useUpsertConsent";
import useConsentAudit from "../../state/hooks/users/useConsentAudit";
import useLogOutAll from "../../state/hooks/users/useLogOutAll";

const Profile = () => {
  const navigate = useNavigate();
  const query = useLocation();
  const userId = query.pathname.replace("/main-users/", "");
  const { data: meData, isLoading: mLoading } = useMe();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const handleUnsubscribe = () => {
    mutate({
      userId,
      data: {
        newsletter: false,
        subscribeToSpecialOffersPushNotifications: false,
        subscribeToLearningEmails: false,
        subscribeToLearningPushNotifications: false,
      },
    });
  };
  
  const { mutate, isPending: UPending } = useUpdateUser({
    onSuccess: () => {
      setShowPassword(false);
      setNewPassword("");
      refetch();
      setFormSubmitting(false);
    },
    onError: (error) => {
      console.error("Update failed:", error);
      alert(`Failed to update: ${error?.response?.data?.message || "Unknown error occurred"}`);
      setFormSubmitting(false);
    },
  });

  const {
    refetch,
    data: UData,
    isLoading,
    isPending,
    isError,
    error,
    isFetching,
  } = useUser(userId);

  const { mutateDeleteMainUser, isPending: UDeletedPending } =
    useDeleteMainUser({
      onSuccess: () => {
        alert("Deleted MainUser success");
        refetch();
      },
      onError: (error) => {
        alert(`Failed to delete MainUser: ${error}`);
      },
    });

  const handleDeleteMainUser = () => {
    const confirmDelete = confirm("Are you sure you want to delete this user?");

    if (confirmDelete) {
      mutateDeleteMainUser({ mainUserId: userId });
    }
  };

  const [logOutAllModalOpen, setLogOutAllModalOpen] = useState(false);

  const { mutate: logOutAll, isPending: isLogOutAllPending } = useLogOutAll({
    onSuccess: () => {
      setLogOutAllModalOpen(false);
    },
  });

  const handleConfirmLogOutAll = () => {
    logOutAll({ userId });
  };

  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordFieldType, setPasswordFieldType] = useState("password");

  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [auditModalConsentId, setAuditModalConsentId] = useState<number | null>(null);
  const [editingConsent, setEditingConsent] = useState<Partial<IConsent> | null>(null);
  const [consentForm, setConsentForm] = useState<{
    optOutType: ConsentOptOutType;
    optedOut: boolean;
    comment: string;
  }>({ optOutType: "OPT_OUT_AI_TRAINING", optedOut: true, comment: "" });

  const CONSENT_OPT_OUT_TYPES: { value: ConsentOptOutType; label: string }[] = [
    { value: "OPT_OUT_AI_TRAINING", label: "OPT_OUT_AI_TRAINING" },
    { value: "OPT_OUT_SELLING_OR_SHARING_DATA", label: "OPT_OUT_SELLING_OR_SHARING_DATA" },
    { value: "OPT_OUT_PROFILING", label: "OPT_OUT_PROFILING" },
  ];

  const {
    data: consentsData,
    isLoading: isConsentsLoading,
    refetch: refetchConsents,
  } = useUserConsents(userId);

  const { mutate: upsertConsent, isPending: isUpsertPending } = useUpsertConsent({
    onSuccess: () => {
      setConsentModalOpen(false);
      setEditingConsent(null);
      setConsentForm({ optOutType: "OPT_OUT_AI_TRAINING", optedOut: true, comment: "" });
      refetchConsents();
    },
  });

  const handleOpenConsentModal = (consent?: IConsent) => {
    if (consent) {
      setEditingConsent(consent);
      setConsentForm({
        optOutType: consent.optOutType,
        optedOut: consent.optedOut,
        comment: consent.comment || "",
      });
    } else {
      setEditingConsent(null);
      setConsentForm({ optOutType: "OPT_OUT_AI_TRAINING", optedOut: true, comment: "" });
    }
    setConsentModalOpen(true);
  };

  const handleCloseConsentModal = () => {
    setConsentModalOpen(false);
    setEditingConsent(null);
    setConsentForm({ optOutType: "OPT_OUT_AI_TRAINING", optedOut: true, comment: "" });
  };

  const handleConsentSubmit = () => {
    upsertConsent({
      userId,
      data: {
        optOutType: consentForm.optOutType,
        optedOut: consentForm.optedOut,
        comment: consentForm.comment || undefined,
      },
    });
  };

  const handleGoToSubscription = (e: React.MouseEvent<HTMLButtonElement>) => {
    const userData = UData as any;
    if (userData && userData.subUsers && userData.subUsers.length > 0) {
      const subUserId = userData.subUsers[0].id;
      const url = `/subscriptions?subUserId=${subUserId}`;
      if (e.metaKey || e.ctrlKey) {window.open(url, "_blank");}
      else {navigate(url);}
    } else {
      alert("No subscription user found for this account");
    }
  };

  const handleGoToSubUsers = (e: React.MouseEvent<HTMLButtonElement>) => {
    const userData = UData as any;
    if (userData && userData.subUsers && userData.subUsers.length > 0) {
      const subUserId = userData.subUsers[0].id;
      const url = `/sub-users/${subUserId}`;
      if (e.metaKey || e.ctrlKey) {window.open(url, "_blank");}
      else {navigate(url);}
    } else {
      alert("No sub users found for this account");
    }
  };

  const {
    mutate: downloadUserExport,
    isPending: isDownloadPending,
    isError: isDownloadError,
    error: downloadError,
  } = useUserExportDownload();

  if (isLoading || isPending || mLoading || isFetching)
    return <div>Loading...</div>;

  if (isError && error) {
    return (
      <div className="flex flex-1 justify-center items-center h-full">
        <Typography>{JSON.stringify(error.response?.data.message)}</Typography>
      </div>
    );
  }

  const initialValues: Partial<User> = {
    email: UData?.email || "",
    verified: UData?.verified || false,
    newsletter: UData?.newsletter || false,
    subscribeToSpecialOffersPushNotifications:
      UData?.subscribeToSpecialOffersPushNotifications || false,
    subscribeToLearningEmails: UData?.subscribeToLearningEmails || false,
    subscribeToLearningPushNotifications:
      UData?.subscribeToLearningPushNotifications || false,
    newPassword: "",
    createdAt: formatDate(UData?.createdAt),
    verificationDate: formatDate(UData?.verificationDate),
  };

  const handleSubmit = async (values: any, { setSubmitting, validateForm }: any) => {
    try {
      setFormSubmitting(true);
      
      // Validate form first
      const errors = await validateForm(values);
      if (Object.keys(errors).length > 0) {
        console.error("Validation errors:", errors);
        alert("Please fix the validation errors before submitting.");
        setFormSubmitting(false);
        setSubmitting(false);
        return;
      }
      
      const { newPassword, ...rest } = getChangedValues(initialValues, values);

      if (Object.keys({ newPassword, ...rest }).length > 0) {
        mutate({
          userId,
          data: { ...rest, password: newPassword },
        });
      } else {
        alert("No changes to update");
        setFormSubmitting(false);
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Error during submission:", error);
      alert(`An error occurred during submission: ${String(error)}`);
      setFormSubmitting(false);
      setSubmitting(false);
    }
  };

  const handleGeneratePassword = (
    setFieldValue: (arg0: string, arg1: string) => void
  ) => {
    const password = generateRandomPassword();
    setNewPassword(password);
    setFieldValue("newPassword", password);
    setShowPassword(true);
    setPasswordFieldType("text"); // Show the generated password as text initially
  };

  return (
    <div className="flex overflow-x-auto px-30 pt-80 pb-20 flex-col bg-light-grey-1 h-full">
      <div className="flex justify-between items-center">
        <h2 className="text-dark-grey font-bold text-2xl">Profile</h2>
        <div className="flex gap-2">
          <Button
            type="secondary"
            buttonType="button"
            id="Profile.GoToSubscriptionButton"
            onClick={handleGoToSubscription}
          >
            Go to Subscription
          </Button>
          <Button
            type="secondary"
            buttonType="button"
            id="Profile.GoToSubscriptionButton"
            onClick={handleGoToSubUsers}
          >
            Go to Sub Users
          </Button>
          <Button
            type="secondary"
            buttonType="button"
            id="Profile.DownloadUsersExportButton"
            onClick={() => downloadUserExport(userId)}
            isLoading={isDownloadPending}
            disabled={isDownloadPending}
          >
            Download Users Export Data
          </Button>
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={mainUserSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {(formik: FormikValues) => (
          <form onSubmit={formik.handleSubmit}>
            <div className="mt-50 mb-30">
              <FormField label="Email (required)">
                <TextInput
                  customStyles="flex-col w-360"
                  placeholder=""
                  customInputStyles="h-12 w-full"
                  type="email"
                  onChange={(e) =>
                    formik.setFieldValue("email", e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  name="email"
                />
                {InputErrorMessage({ props: formik, type: "email" })}
              </FormField>
              <FormField label="Creation Date">
                <TextInput
                  customStyles="flex-col w-360"
                  placeholder=""
                  customInputStyles="h-12 w-full"
                  onChange={(e) =>
                    formik.setFieldValue("creationDate", e.target.value)
                  }
                  type="text"
                  onBlur={formik.handleBlur}
                  value={formik.values.createdAt}
                  name="creationDate"
                  id="Profile.CreationDate"
                  disabled
                />
              </FormField>
              {
                (UData as any)?.subUsers && (UData as any)?.subUsers.length > 0 && (
                  <div>
                    <Typography className="mb-10 font-semibold">Sub Users</Typography>
                    {(UData as any).subUsers.map((subUser: any, index: number) => (
                      <FormField key={subUser.id} label={`SubUser ${index + 1}`}>
                        <TextInput
                          customStyles="flex-col w-360"
                          placeholder=""
                          customInputStyles="h-12 w-full"
                          onChange={(e) =>
                            formik.setFieldValue(`subUserId_${index}`, e.target.value)
                          }
                          type="text"
                          onBlur={formik.handleBlur}
                          value={subUser.id}
                          name={`subUserId_${index}`}
                          id={`Profile.SubUserId_${index}`}
                          disabled
                        />
                      </FormField>
                    ))}
                  </div>
                )
              }
              <FormField label="Verification Date">
                <TextInput
                  customStyles="flex-col w-360"
                  placeholder=""
                  customInputStyles="h-12 w-full"
                  onChange={(e) =>
                    formik.setFieldValue("verificationDate", e.target.value)
                  }
                  type="text"
                  onBlur={formik.handleBlur}
                  value={formik.values.verificationDate}
                  name="verificationDate"
                  id="Profile.VerificationDate"
                  disabled
                />
              </FormField>
              <FormField label="Verified">
                <input
                  type="checkbox"
                  onChange={(e) =>
                    formik.setFieldValue("verified", e.target.checked)
                  }
                  onBlur={formik.handleBlur}
                  checked={formik.values.verified}
                  name="verified"
                  id="Profile.Verified"
                />
              </FormField>
              <FormField label="NewsLetter">
                <input
                  type="checkbox"
                  onChange={(e) =>
                    formik.setFieldValue("newsletter", e.target.checked)
                  }
                  onBlur={formik.handleBlur}
                  checked={formik.values.newsletter}
                  name="newsletter"
                  id="Profile.NewsLetter"
                />
              </FormField>
              <FormField label="SubscribeToLearningEmails">
                <input
                  type="checkbox"
                  onChange={(e) =>
                    formik.setFieldValue(
                      "subscribeToLearningEmails",
                      e.target.checked
                    )
                  }
                  onBlur={formik.handleBlur}
                  checked={formik.values.subscribeToLearningEmails}
                  name="subscribeToLearningEmails"
                  id="Profile.SubscribeToLearningEmails"
                />
              </FormField>
              <FormField label="SubscribeToLearningPushNotifications">
                <input
                  type="checkbox"
                  onChange={(e) =>
                    formik.setFieldValue(
                      "subscribeToLearningPushNotifications",
                      e.target.checked
                    )
                  }
                  onBlur={formik.handleBlur}
                  checked={formik.values.subscribeToLearningPushNotifications}
                  name="subscribeToLearningPushNotifications"
                  id="Profile.SubscribeToLearningPushNotifications"
                />
              </FormField>
              <FormField label="subscribeToSpecialOffersPushNotifications">
                <input
                  type="checkbox"
                  onChange={(e) =>
                    formik.setFieldValue(
                      "subscribeToSpecialOffersPushNotifications",
                      e.target.checked
                    )
                  }
                  onBlur={formik.handleBlur}
                  checked={
                    formik.values.subscribeToSpecialOffersPushNotifications
                  }
                  name="subscribeToSpecialOffersPushNotifications"
                  id="Profile.SubscribeToSpecialOffersPushNotifications"
                />
              </FormField>
               <div className="mb-8">
              <Button
                type="main"
                buttonType="button"
                id="Profile.UnsubscribeButton"
                onClick={handleUnsubscribe}
                isLoading={UPending}
                disabled={UPending || formSubmitting}
              >
                Unsubscribe
              </Button>
              </div>
              <FormField label="Set New Password">
                <Button
                  type="secondary"
                  buttonType="button"
                  onClick={() => handleGeneratePassword(formik.setFieldValue)}
                >
                  Set New Password
                </Button>
                {showPassword && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: "10px",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                      }}
                    >
                      <TextInput
                        customStyles="flex-col w-360"
                        placeholder=""
                        customInputStyles="h-12 w-full"
                        type={passwordFieldType}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          formik.setFieldValue("newPassword", e.target.value);
                        }}
                        value={formik.values.newPassword || newPassword}
                        name="newPassword"
                      />
                      <div style={{ position: "absolute", bottom: "-20px" }}>
                        {InputErrorMessage({
                          props: formik,
                          type: "newPassword",
                        })}
                      </div>
                    </div>
                    <div style={{ display: "flex", marginLeft: "10px" }}>
                      <Button
                        type="secondary"
                        buttonType="button"
                        onClick={() =>
                          setPasswordFieldType(
                            passwordFieldType === "text" ? "password" : "text"
                          )
                        }
                        style={{ marginLeft: "10px" }}
                      >
                        {passwordFieldType === "text" ? "Hide" : "Show"}
                      </Button>
                      <Button
                        type="secondary"
                        buttonType="button"
                        onClick={() => {
                          setNewPassword("");
                          setShowPassword(false);
                          formik.setFieldValue("newPassword", "");
                        }}
                        style={{ marginLeft: "10px" }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </FormField>
              <FormField label="Force log out">
                <Button
                  type="secondary"
                  buttonType="button"
                  id="Profile.LogOutAllButton"
                  isLoading={isLogOutAllPending}
                  disabled={isLogOutAllPending}
                  onClick={() => setLogOutAllModalOpen(true)}
                >
                  Log out of all devices
                </Button>
              </FormField>
              <FormField label="Delete user">
                <Button
                  type="secondary"
                  buttonType="button"
                  id="Profile.DeleteUserButton"
                  disabled={formik.values.email
                    .toLowerCase()
                    .includes("anonymous")}
                  isLoading={UDeletedPending}
                  onClick={handleDeleteMainUser}
                >
                  Delete
                </Button>
              </FormField>
            </div>
            <div>
              <Button
                type="main"
                id="Profile.UpdateButton"
                isLoading={UPending || formSubmitting}
                buttonType="submit"
                disabled={UPending || formSubmitting}
              >
                {UPending || formSubmitting ? "Updating..." : "Update"}
              </Button>
            </div>
          </form>
        )}
      </Formik>

      {/* ─── Consents Section ─────────────────────────────────────────── */}
      <div className="mt-50 mb-30">
        <div className="flex justify-between items-center mb-20">
          <h3 className="text-dark-grey font-bold text-xl">Consents</h3>
          <Button
            type="secondary"
            buttonType="button"
            id="Profile.AddConsentButton"
            onClick={() => handleOpenConsentModal()}
          >
            + Add Consent
          </Button>
        </div>

        {isConsentsLoading ? (
          <Typography>Loading consents...</Typography>
        ) : (
          <>
            {(!consentsData?.consents || consentsData.consents.length === 0) && (
              <Typography className="text-dark-grey">No consents recorded for this user.</Typography>
            )}

            {consentsData?.consents && consentsData.consents.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-light-grey-2 text-left">
                      <th className="px-16 py-10 font-semibold text-dark-grey border-b border-light-grey-3">Type</th>
                      <th className="px-16 py-10 font-semibold text-dark-grey border-b border-light-grey-3">Status</th>
                      <th className="px-16 py-10 font-semibold text-dark-grey border-b border-light-grey-3">Comment</th>
                      <th className="px-16 py-10 font-semibold text-dark-grey border-b border-light-grey-3">Updated</th>
                      <th className="px-16 py-10 font-semibold text-dark-grey border-b border-light-grey-3">Created</th>
                      <th className="px-16 py-10 font-semibold text-dark-grey border-b border-light-grey-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consentsData.consents.map((consent) => (
                      <tr
                        key={consent.id}
                        className="border-b border-light-grey-3 hover:bg-light-grey-1"
                      >
                        <td className="px-16 py-10 text-dark-grey font-mono text-xs">{consent.optOutType}</td>
                        <td className="px-16 py-10">
                          <span
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 999,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              ...(consent.optedOut
                                ? { backgroundColor: "#fde8e8", color: "#c0392b" }
                                : { backgroundColor: "#fef3cd", color: "#b07d00" }),
                            }}
                          >
                            {consent.optedOut ? "Opted Out" : "Opted In"}
                          </span>
                        </td>
                        <td className="px-16 py-10 text-dark-grey">{consent.comment ?? "—"}</td>
                        <td className="px-16 py-10 text-dark-grey">{formatDate(consent.updatedAt)}</td>
                        <td className="px-16 py-10 text-dark-grey">{formatDate(consent.createdAt)}</td>
                        <td className="px-16 py-10">
                          <div className="flex gap-8">
                            <Button
                              type="secondary"
                              buttonType="button"
                              id={`Profile.EditConsent_${consent.id}`}
                              onClick={() => handleOpenConsentModal(consent)}
                            >
                              Edit
                            </Button>
                            <Button
                              type="secondary"
                              buttonType="button"
                              id={`Profile.AuditConsent_${consent.id}`}
                              onClick={() => setAuditModalConsentId(consent.id)}
                            >
                              Logs
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Force Log Out Confirmation Modal ─────────────────────────── */}
      <Modal
        title={
          <span className="text-dark-grey font-bold text-base">
            Log out of all devices
          </span>
        }
        open={logOutAllModalOpen}
        onCancel={() => setLogOutAllModalOpen(false)}
        footer={
          <div className="flex justify-end gap-8">
            <Button
              type="secondary"
              buttonType="button"
              id="LogOutAllModal.CancelButton"
              border
              style={{ whiteSpace: "nowrap", flexShrink: 0 }}
              onClick={() => setLogOutAllModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="main"
              buttonType="button"
              id="LogOutAllModal.ConfirmButton"
              isLoading={isLogOutAllPending}
              disabled={isLogOutAllPending}
              style={{ whiteSpace: "nowrap", flexShrink: 0 }}
              onClick={handleConfirmLogOutAll}
            >
              Yes, log out
            </Button>
          </div>
        }
        width={600}
        destroyOnClose
      >
        <div className="flex flex-col gap-10 py-10">
          <Typography>
            Are you sure you want to log out every sub-account of{" "}
            <span className="font-semibold">{UData?.email}</span> on both web and
            app?
          </Typography>
          <p className="text-sm" style={{ color: "#9ca3af" }}>
            Sign-in sessions stop working immediately. Magic links already sent
            by email keep working, and access tokens already issued stay valid
            until they expire (up to 1 hour).
          </p>
        </div>
      </Modal>

      {/* ─── Add / Edit Consent Modal ─────────────────────────────────── */}
      <Modal
        title={
          <span className="text-dark-grey font-bold text-base">
            {editingConsent ? "Edit Consent" : "Add Consent"}
          </span>
        }
        open={consentModalOpen}
        onCancel={handleCloseConsentModal}
        footer={
          <div className="flex justify-end gap-8">
            <Button
              type="secondary"
              buttonType="button"
              id="ConsentModal.CancelButton"
              onClick={handleCloseConsentModal}
            >
              Cancel
            </Button>
            <Button
              type="main"
              buttonType="button"
              id="ConsentModal.SaveButton"
              isLoading={isUpsertPending}
              disabled={isUpsertPending}
              onClick={handleConsentSubmit}
            >
              {isUpsertPending ? "Saving..." : "Save"}
            </Button>
          </div>
        }
        width={480}
        destroyOnClose
      >
        <div className="flex flex-col gap-20 py-10">
          <div className="flex flex-col gap-6">
            <label className="text-sm font-semibold text-dark-grey">Opt-Out Type</label>
            <Select
              value={consentForm.optOutType}
              onChange={(value) =>
                setConsentForm((prev) => ({ ...prev, optOutType: value }))
              }
              options={CONSENT_OPT_OUT_TYPES}
              size="large"
              style={{ width: "100%" }}
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-dark-grey">Opted Out</label>
            <Switch
              checked={consentForm.optedOut}
              onChange={(checked) =>
                setConsentForm((prev) => ({ ...prev, optedOut: checked }))
              }
              checkedChildren="Opted Out"
              unCheckedChildren="Opted In"
            />
          </div>

          <div className="flex flex-col gap-6">
            <label className="text-sm font-semibold text-dark-grey">Comment <span className="font-normal" style={{ color: "#9ca3af" }}>(optional)</span></label>
            <Input.TextArea
              value={consentForm.comment}
              onChange={(e) =>
                setConsentForm((prev) => ({ ...prev, comment: e.target.value }))
              }
              placeholder="Add a comment..."
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* ─── Audit Log Modal ──────────────────────────────────────────── */}
      <Modal
        title={
          <span className="text-dark-grey font-bold text-base">Consent Audit History</span>
        }
        open={auditModalConsentId !== null}
        onCancel={() => setAuditModalConsentId(null)}
        footer={
          <div className="flex justify-end">
            <Button
              type="secondary"
              buttonType="button"
              id="AuditModal.CloseButton"
              border
              onClick={() => setAuditModalConsentId(null)}
            >
              Close
            </Button>
          </div>
        }
        width="80%"
        style={{ maxWidth: 860 }}
        destroyOnClose
      >
        {auditModalConsentId !== null && (
          <ConsentAuditPanel userId={userId} consentId={auditModalConsentId} />
        )}
      </Modal>
    </div>
  );
};

interface ConsentAuditPanelProps {
  userId: string;
  consentId: number;
}

const ConsentAuditPanel = ({ userId, consentId }: ConsentAuditPanelProps) => {
  const { data, isLoading, isError } = useConsentAudit(userId, consentId);

  const columns: ColumnsType<IConsentAuditLog> = [
    {
      title: "Modified By",
      dataIndex: "modifiedByUserEmail",
      key: "modifiedByUserEmail",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "optedOut",
      key: "optedOut",
      width: 120,
      render: (optedOut: boolean) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 999,
            fontSize: "0.72rem",
            fontWeight: 600,
            whiteSpace: "nowrap",
            ...(optedOut
              ? { backgroundColor: "#fde8e8", color: "#c0392b" }
              : { backgroundColor: "#fef3cd", color: "#b07d00" }),
          }}
        >
          {optedOut ? "Opted Out" : "Opted In"}
        </span>
      ),
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      ellipsis: true,
      render: (comment: string | null) => comment ?? "—",
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (date: string) => formatDate(date),
    },
  ];

  if (isError) return <Typography>Failed to load audit logs.</Typography>;

  return (
    <Table<IConsentAuditLog>
      dataSource={data?.auditLogs ?? []}
      columns={columns}
      rowKey="id"
      loading={isLoading}
      pagination={false}
      size="small"
      locale={{ emptyText: "No audit history found." }}
    />
  );
};

export default Profile;
